import { Popconfirm, Select, Tooltip } from "antd";
import { ColumnProps } from "antd/lib/table";
import { renderMasterIndex } from "helpers/table";
import { TFunction } from "i18next";
import { FileTemplate } from "models/FileTemplate";
import { FileTemplateInput } from "models/FileTemplateInput";
import React, { useEffect, useState } from "react";
import {
  FormItem,
  InputNumber,
  InputText,
  LayoutCell,
  LayoutHeader,
} from "react3l-ui-library";
import nameof from "ts-nameof.macro";
import TrashCan16 from "@carbon/icons-react/es/trash-can/16";
import { DECIMAL } from "react3l-ui-library/build/components/Input/InputNumber";
import { v4 as uuidv4 } from "uuid";
import { menuReportRepository } from "repositories/menu-report-repository";
import { utilService } from "services/common-services/util-service";
import { RowSelectionType } from "antd/lib/table/interface";
import "./MenuReportDetail.scss";
import appMessageService from "services/common-services/app-message-service";
import _cloneDeep from "lodash/cloneDeep";
import TextArea from "antd/lib/input/TextArea";

type KeyType = string | number;
type Loading = "default" | "submitting" | "submitted" | "error";

enum FileTemplateInputEnum {
  UPDATE_ALL,
  UPDATE_FILE_TEMPLATE_INPUTS,
  UPDATE_VISIBLE,
  UPDATE_CURRENT_FILE_TEMPLATE,
}

interface FileTemplateInputState {
  fileTemplateInputs?: FileTemplateInput[];
  visible?: boolean;
  fileTemplate?: FileTemplate;
}

interface FileTemplateInputAction {
  type: FileTemplateInputEnum;
  data: FileTemplateInputState;
}

const LIST_CONTROLS: any[] = [
  {
    id: 1,
    code: "text",
    value: 1,
    label: "Text",
    name: "Text",
  },
  {
    id: 2,
    code: "select",
    value: 2,
    label: "Select",
    name: "Select",
  },
  {
    id: 3,
    code: "textarea",
    value: 3,
    label: "Textarea",
    name: "Textarea",
  },
  // {
  //   id: 4,
  //   code: "radio",
  //   value: 4,
  //   label: "Radio",
  //   name: "Radio",
  // },
  // {
  //   id: 5,
  //   code: "Checkbox",
  //   value: 5,
  //   label: "Checkbox",
  //   name: "Checkbox",
  // },
  {
    id: 6,
    code: "upload",
    value: 6,
    label: "Upload",
    name: "Upload",
  },
  {
    id: 7,
    code: "textEditor",
    value: 7,
    label: "Text Editor",
    name: "Text Editor",
  },
  {
    id: 8,
    code: "date",
    value: 8,
    label: "Date",
    name: "Date",
  },
  {
    id: 9,
    code: "number",
    value: 9,
    label: "Number",
    name: "Number",
  },
];

function fileTemplateInputReducer(
  state: FileTemplateInputState,
  action: FileTemplateInputAction
): FileTemplateInputState {
  switch (action.type) {
    case FileTemplateInputEnum.UPDATE_ALL:
      return {
        ...state,
        fileTemplateInputs: [...action.data.fileTemplateInputs],
        visible: action.data.visible,
        fileTemplate: { ...action.data.fileTemplate },
      };
    case FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS:
      return {
        ...state,
        fileTemplateInputs: [...action.data.fileTemplateInputs],
      };
    case FileTemplateInputEnum.UPDATE_VISIBLE:
      return {
        ...state,
        visible: action.data.visible,
      };
  }
}

export function useFileTemplateInputModalHook(
  updateFileTempalateInputs: (
    fileTemplateInputs: FileTemplateInput[],
    fileTemplate: FileTemplate
  ) => void,
  translate: TFunction
) {
  const [
    { fileTemplateInputs: fileTemplateInputsInternal, visible, fileTemplate },
    dispatchValue,
  ] = React.useReducer(fileTemplateInputReducer, {
    fileTemplateInputs: [],
    visible: false,
  });

  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [loadingType, setLoadingType] = React.useState<Loading>("default");

  const handleAddFileTemplateInput = React.useCallback(() => {
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
      data: {
        fileTemplateInputs: [
          { ...new FileTemplateInput(), key: uuidv4() },
          ...fileTemplateInputsInternal,
        ],
      },
    });
  }, [fileTemplateInputsInternal]);

  useEffect(() => {
    if (isFirstLoad === false && fileTemplateInputsInternal?.length > 0) {
      const fileTemplateInputsInternalHere = _cloneDeep(
        fileTemplateInputsInternal
      );
      for (let i = 0; i < fileTemplateInputsInternalHere?.length; i++) {
        fileTemplateInputsInternalHere[i].key = uuidv4();
      }
      setIsFirstLoad(true);
      dispatchValue({
        type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
        data: {
          fileTemplateInputs: [...fileTemplateInputsInternalHere],
        },
      });
    }
  }, [fileTemplateInputsInternal, isFirstLoad]);

  const handleChangeFileTemplateInputCell = React.useCallback(
    (index: number, fieldKey: string) => (value: string | number) => {
      const fileTemplateInputValues = [...fileTemplateInputsInternal];
      const currentFileTemplateInput = fileTemplateInputValues[index];
      currentFileTemplateInput[fieldKey] = value;
      dispatchValue({
        type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
        data: {
          fileTemplateInputs: fileTemplateInputValues,
        },
      });
    },
    [fileTemplateInputsInternal]
  );

  const handleChangeFileTemplateInputCellTexArea = React.useCallback(
    (index: number, fieldKey: string, value: any) => {
      const fileTemplateInputValues = [...fileTemplateInputsInternal];
      const currentFileTemplateInput = fileTemplateInputValues[index];
      currentFileTemplateInput[fieldKey] = value?.currentTarget?.value;
      dispatchValue({
        type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
        data: {
          fileTemplateInputs: fileTemplateInputValues,
        },
      });
    },
    [fileTemplateInputsInternal]
  );

  const handleDeleteRow = React.useCallback(
    (index: number) => () => {
      if (index !== null) {
        fileTemplateInputsInternal.splice(index, 1);
        dispatchValue({
          type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
          data: {
            fileTemplateInputs: fileTemplateInputsInternal,
          },
        });
      }
    },
    [fileTemplateInputsInternal]
  );

  const handleOpenModal = React.useCallback((fileTemplate: FileTemplate) => {
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_ALL,
      data: {
        fileTemplateInputs:
          fileTemplate && fileTemplate.fileTemplateInputs
            ? fileTemplate.fileTemplateInputs
            : [],
        visible: true,
        fileTemplate: fileTemplate,
      },
    });
    setIsFirstLoad(false);
  }, []);

  const handleCloseModal = React.useCallback(() => {
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_VISIBLE,
      data: {
        visible: false,
      },
    });
    setIsFirstLoad(false);
  }, []);

  const { notifyUpdateItemError } = appMessageService.useCRUDMessage();

  const handleSaveModal = React.useCallback(() => {
    setLoadingType("submitting");
    menuReportRepository
      .validateInputs({
        ...new FileTemplate(),
        fileTemplateInputs: [...fileTemplateInputsInternal],
      })
      .subscribe({
        next: (res: FileTemplate) => {
          setLoadingType("default");

          // validate khi BE trả lỗi (check bằng biến isError)
          if (res && res.isError) {
            dispatchValue({
              type: FileTemplateInputEnum.UPDATE_VISIBLE,
              data: {
                visible: true,
              },
            });
            notifyUpdateItemError();
            dispatchValue({
              type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
              data: {
                fileTemplateInputs: res.fileTemplateInputs,
              },
            });
            updateFileTempalateInputs(fileTemplateInputsInternal, fileTemplate);
          }

          if (res && !res.isError) {
            dispatchValue({
              type: FileTemplateInputEnum.UPDATE_VISIBLE,
              data: {
                visible: false,
              },
            });
            updateFileTempalateInputs(fileTemplateInputsInternal, fileTemplate);
          }
        },
        error: () => {
          setLoadingType("error");
        },
      });
  }, [
    fileTemplate,
    fileTemplateInputsInternal,
    notifyUpdateItemError,
    updateFileTempalateInputs,
  ]);

  const [
    selectedRowKeysFileTemplateInput,
    setSelectedRowKeysFileTemplateInput,
  ] = React.useState<KeyType[]>([]);

  const rowSelectionFileTemplateInput = React.useMemo(
    () => ({
      onChange(selectedRowKeys: KeyType[]) {
        setSelectedRowKeysFileTemplateInput(selectedRowKeys);
      },
      selectedRowKeysFileTemplateInput,
      type: "checkbox" as RowSelectionType,
      getCheckboxProps: (record: FileTemplate) => ({
        disabled: record.used,
      }),
    }),
    [selectedRowKeysFileTemplateInput]
  );

  const canBulkActionFileTemplateInput = React.useMemo(
    () => selectedRowKeysFileTemplateInput.length > 0,
    [selectedRowKeysFileTemplateInput.length]
  );

  const handleChangeFieldType = React.useCallback(
    (index: number, fieldKey: string) => (value: string | number) => {
      const fileTemplateInputValues = [...fileTemplateInputsInternal];
      const currentFileTemplateInput = fileTemplateInputValues[index];
      currentFileTemplateInput[fieldKey] = value;
      const foundConfig = LIST_CONTROLS.find(
        (e) => e.id === currentFileTemplateInput.fileTemplateInputTypeId
      );
      if (foundConfig) {
        currentFileTemplateInput[`fileTemplateInputType`] = foundConfig;
      }

      dispatchValue({
        type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
        data: {
          fileTemplateInputs: fileTemplateInputValues,
        },
      });
    },
    [fileTemplateInputsInternal]
  );

  const handleBulkDeleteFileTemplateInput = React.useCallback(() => {
    const selectedRowKeysValue = [...selectedRowKeysFileTemplateInput];
    const listFileTemplateInputValue = [...fileTemplateInputsInternal].filter(
      (currentFile: FileTemplate) =>
        !selectedRowKeysValue.includes(currentFile.key)
    );
    dispatchValue({
      type: FileTemplateInputEnum.UPDATE_FILE_TEMPLATE_INPUTS,
      data: {
        fileTemplateInputs: [...listFileTemplateInputValue],
      },
    });
    setSelectedRowKeysFileTemplateInput([]);
  }, [fileTemplateInputsInternal, selectedRowKeysFileTemplateInput]);

  const columns: ColumnProps<FileTemplateInput>[] = React.useMemo(() => {
    return [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("general.columns.index")}
          />
        ),
        key: "index",
        width: 80,
        align: "center",
        fixed: true,
        render: renderMasterIndex<FileTemplateInput>(),
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.fieldName")}
          />
        ),
        key: nameof("fieldName"),
        width: 250,
        dataIndex: nameof("fieldName"),
        render(...params: [string, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  params[1],
                  "fieldName"
                )}
                message={utilService.getValidateMessage(params[1], "fieldName")}
              >
                <TextArea
                  value={params[0]}
                  placeholder={translate(
                    "fileTemplateInputs.placeholder.fieldName"
                  )}
                  onChange={(e) =>
                    handleChangeFileTemplateInputCellTexArea(
                      params[2],
                      "fieldName",
                      e
                    )
                  }
                  rows={1}
                  className="text-area-base-data-dxg"
                  allowClear
                />
                {/* <InputText
                  type={0}
                  value={params[0]}
                  placeHolder={translate(
                    "fileTemplateInputs.placeholder.fieldName"
                  )}
                  onChange={handleChangeFileTemplateInputCell(
                    params[2],
                    "fieldName"
                  )}
                /> */}
              </FormItem>
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.label")}
          />
        ),
        key: nameof("label"),
        width: 200,
        dataIndex: nameof("label"),
        render(...params: [string, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  params[1],
                  "label"
                )}
                message={utilService.getValidateMessage(params[1], "label")}
              >
                <TextArea
                  value={params[0]}
                  placeholder={translate(
                    "fileTemplateInputs.placeholder.label"
                  )}
                  onChange={(e) =>
                    handleChangeFileTemplateInputCellTexArea(
                      params[2],
                      "label",
                      e
                    )
                  }
                  rows={1}
                  className="text-area-base-data-dxg"
                  allowClear
                />
                {/* <InputText
                  type={0}
                  value={params[0]}
                  placeHolder={translate(
                    "fileTemplateInputs.placeholder.label"
                  )}
                  onChange={handleChangeFileTemplateInputCell(
                    params[2],
                    "label"
                  )}
                /> */}
              </FormItem>
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.fieldValue")}
          />
        ),
        key: nameof("fieldValue"),
        width: 500,
        dataIndex: nameof("fieldValue"),
        render(...params: [string, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  params[1],
                  "fieldValue"
                )}
                message={utilService.getValidateMessage(
                  params[1],
                  "fieldValue"
                )}
              >
                <TextArea
                  value={params[0]}
                  placeholder={translate(
                    "fileTemplateInputs.placeholder.fieldValue"
                  )}
                  onChange={(e) =>
                    handleChangeFileTemplateInputCellTexArea(
                      params[2],
                      "fieldValue",
                      e
                    )
                  }
                  rows={1}
                  className="text-area-base-data-dxg"
                  allowClear
                />
              </FormItem>
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.order")}
            className="p-1"
          />
        ),
        width: 100,
        key: nameof("order"),
        dataIndex: nameof("order"),
        render(...params: [number, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  params[1],
                  "order"
                )}
                message={utilService.getValidateMessage(params[1], "order")}
              >
                {" "}
                <InputNumber
                  value={params[0]}
                  onChange={handleChangeFileTemplateInputCell(
                    params[2],
                    "order"
                  )}
                  numberType={DECIMAL}
                  placeHolder={translate(
                    "fileTemplateInputs.placeholder.order"
                  )}
                  type={0}
                />
              </FormItem>
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.fileTemplateInputTypeId")}
          />
        ),
        width: 150,
        key: nameof("fileTemplateInputTypeId"),
        dataIndex: nameof("fileTemplateInputTypeId"),
        render(...params: [number, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  params[1],
                  "fileTemplateInputTypeId"
                )}
                message={utilService.getValidateMessage(
                  params[1],
                  "fileTemplateInputTypeId"
                )}
              >
                {" "}
                <Select
                  value={params[0]}
                  style={{ width: 120 }}
                  onChange={handleChangeFieldType(
                    params[2],
                    "fileTemplateInputTypeId"
                  )}
                  options={LIST_CONTROLS}
                />
              </FormItem>
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.columnNumber")}
          />
        ),
        width: 100,
        key: nameof("columnNumber"),
        dataIndex: nameof("columnNumber"),
        render(...params: [number, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  params[1],
                  "columnNumber"
                )}
                message={utilService.getValidateMessage(
                  params[1],
                  "columnNumber"
                )}
              >
                {" "}
                <InputNumber
                  value={params[0]}
                  onChange={handleChangeFileTemplateInputCell(
                    params[2],
                    "columnNumber"
                  )}
                  numberType={DECIMAL}
                  type={0}
                  min={1}
                  max={24}
                />
              </FormItem>
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.dataSource")}
          />
        ),
        key: nameof("dataSource"),
        width: 300,
        dataIndex: nameof("dataSource"),
        render(...params: [string, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <FormItem
                validateStatus={utilService.getValidateStatus(
                  params[1],
                  "dataSource"
                )}
                message={utilService.getValidateMessage(
                  params[1],
                  "dataSource"
                )}
              >
                <Tooltip title={params[0]}>
                  <div className="w-100">
                    <InputText
                      type={0}
                      value={params[0]}
                      placeHolder={translate("fileTemplateInputs.dataSource")}
                      onChange={handleChangeFileTemplateInputCell(
                        params[2],
                        "dataSource"
                      )}
                    />
                  </div>
                </Tooltip>
              </FormItem>
            </LayoutCell>
          );
        },
      },
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("fileTemplateInputs.isDisplayOrderNumber")}
          />
        ),
        width: 100,
        key: nameof("isDisplayOrderNumber"),
        dataIndex: nameof("isDisplayOrderNumber"),
        render(...params: [number, FileTemplateInput, number]) {
          return (
            <LayoutCell orderType="left" tableSize="lg">
              <FormItem>
                <Select
                  value={params[0]}
                  style={{ width: 100 }}
                  onChange={handleChangeFileTemplateInputCell(
                    params[2],
                    "isDisplayOrderNumber"
                  )}
                  options={[
                    {
                      value: 0,
                      label: "No",
                    },
                    {
                      value: 1,
                      label: "Yes",
                    },
                  ]}
                />
              </FormItem>
            </LayoutCell>
          );
        },
      },
      {
        key: nameof("action"),
        dataIndex: nameof("action"),
        fixed: "right",
        width: 80,
        align: "center",
        render(...params: [string, FileTemplateInput, number]) {
          return (
            <Popconfirm
              title={translate("general.warnings.delete")}
              onConfirm={handleDeleteRow(params[2])}
              okText={translate("general.actions.accept")}
              cancelText={translate("general.actions.deny")}
            >
              <TrashCan16 color="red" />
            </Popconfirm>
          );
        },
      },
    ];
  }, [
    translate,
    handleChangeFileTemplateInputCell,
    handleDeleteRow,
    handleChangeFieldType,
    handleChangeFileTemplateInputCellTexArea,
  ]);

  return {
    loadingType,
    selectedRowKeysFileTemplateInput,
    rowSelectionFileTemplateInput,
    canBulkActionFileTemplateInput,
    setSelectedRowKeysFileTemplateInput,
    handleBulkDeleteFileTemplateInput,
    handleOpenModal,
    handleSaveModal,
    handleCloseModal,
    handleAddFileTemplateInput,
    handleChangeFileTemplateInputCell,
    fileTemplateInputColumns: columns,
    fileTemplateInputsInternal,
    visible,
  };
}
